import React from 'react';
import { useLocation, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import BackToTop from '../components/layout/BackToTop';
import SponsorReferralModal from '../components/forms/SponsorReferralModal';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import feesContent from '../content/fees/index.json';
import { siteStage } from '../config/siteStage';
import { usePageMeta } from '../lib/usePageMeta';
import * as Icons from 'lucide-react';
import {
  Check, Home, Users, HandCoins, Handshake, Rocket, Scale,
  PlusCircle, ArrowRight, Info, ShieldCheck, HelpCircle, Calculator,
} from 'lucide-react';

const getIcon = (iconName, className) => {
  const IconComponent = Icons[iconName];
  return IconComponent ? <IconComponent className={className} /> : null;
};

const SectionHeading = ({ id, icon: Icon, title, subtitle }) => (
  <div id={id} className="mb-8 scroll-mt-28">
    <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-3">
      <Icon className="w-4 h-4" />
      {subtitle}
    </div>
    <h2 className="text-2xl sm:text-3xl font-bold text-[#1F1E1E]">{title}</h2>
  </div>
);

const FeesPage = () => {
  const location = useLocation();
  const [sponsorOpen, setSponsorOpen] = React.useState(false);
  const d = feesContent;

  usePageMeta(
    'Plans, sponsorship and fees — ResiAX',
    'How ResiAX pricing works: resident subscriptions, Founder membership, landlord-sponsored and housing-provider-sponsored resident access, the portable occupant passport, and how we avoid duplicate subscription charges.'
  );

  React.useEffect(() => {
    // While public pricing is hidden, keep this page out of search indexes.
    if (!siteStage.publicPricingEnabled) {
      let robots = document.querySelector('meta[name="robots"]');
      if (!robots) {
        robots = document.createElement('meta');
        robots.setAttribute('name', 'robots');
        document.head.appendChild(robots);
      }
      robots.setAttribute('content', 'noindex, nofollow');
    }
    const ldJson = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (feesContent.faq.items || []).map((item) => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: (item.answer || []).join(' ') },
      })),
    };
    let script = document.getElementById('fees-faq-jsonld');
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'fees-faq-jsonld';
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(ldJson);
    if (location.hash) {
      setTimeout(() => {
        const el = document.querySelector(location.hash);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 200);
    } else {
      window.scrollTo(0, 0);
    }
    return () => {
      document.getElementById('fees-faq-jsonld')?.remove();
      document.querySelector('meta[name="robots"]')?.remove();
    };
  }, [location.hash]);

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main>
        {!siteStage.publicPricingEnabled && (
          <div className="pt-24 -mb-8 bg-white">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="fees-pilot-stage-notice">
                <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800 leading-relaxed">
                  <p className="mb-2">
                    <strong>Pilot stage.</strong> ResiAX is currently validating its resident and provider
                    offers through pilot programmes. The subscription tiers on this page are preserved as
                    provisional future pricing and are not currently being offered as a full public catalogue.
                  </p>
                  <p>
                    <Link to="/resident-pilot#register" className="font-semibold underline hover:text-amber-950">Join the resident pilot</Link>
                    {' '}or{' '}
                    <Link to="/providers#collaborate" className="font-semibold underline hover:text-amber-950">discuss a provider pilot</Link>.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Hero */}
        <section className="pt-32 pb-14 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#B5541B] mb-4">
              <span className="w-8 h-px bg-[#B5541B]" />
              {d.hero.badge}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#1F1E1E] leading-tight mb-6" data-testid="fees-heading">
              {d.hero.title}
            </h1>
            <p className="text-lg text-[#3B3535]/75 leading-relaxed max-w-2xl mb-4">
              {d.hero.description}
            </p>
            <p className="text-sm text-[#3B3535]/60 leading-relaxed max-w-2xl">
              {d.hero.note}
            </p>
          </div>
        </section>

        {/* Resident plans */}
        <section className="py-16 bg-[#FFFAF8]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="resident-plans" icon={Home} subtitle="For residents" title={d.residentPlans.heading} />
            <div className="grid md:grid-cols-2 gap-5">
              {(d.residentPlans.items || []).map((p) => (
                <div key={p.name} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-testid={`fees-resident-${p.name.toLowerCase()}`}>
                  <div className="flex items-baseline justify-between mb-3">
                    <h3 className="font-bold text-[#1F1E1E]">{p.name}</h3>
                    <span className="text-[#B5541B] font-bold">{p.price}</span>
                  </div>
                  <p className="text-sm text-[#3B3535]/75 leading-relaxed">{p.note}</p>
                </div>
              ))}
            </div>
            {siteStage.publicPricingEnabled && (
              <Link to="/#plans" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14]">
                Compare resident plans in full <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </section>

        {/* Provider plans */}
        <section className="py-16 bg-white">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="provider-plans" icon={Users} subtitle="For providers" title={d.providerPlans.heading} />
            <p className="text-[#3B3535]/75 leading-relaxed mb-8 max-w-3xl">
              {d.providerPlans.intro}
            </p>
            <div className="grid md:grid-cols-2 gap-5">
              {(d.providerPlans.items || []).map((p) => (
                <div key={p.name} className="bg-[#FFF8F5] rounded-2xl p-6 border border-[#B5541B]/10" data-testid={`fees-provider-${p.name.toLowerCase()}`}>
                  <div className="flex items-baseline justify-between mb-1">
                    <h3 className="font-bold text-[#1F1E1E]">Provider {p.name}</h3>
                    <span className="text-[#B5541B] font-bold">{p.price}</span>
                  </div>
                  <p className="text-xs text-[#3B3535]/60 mb-4">{p.alt}</p>
                  <ul className="space-y-1.5 text-sm text-[#3B3535]/80 mb-4">
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />{p.homes}</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />{p.operators}</li>
                    <li className="flex items-start gap-2"><Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />{p.extra}</li>
                  </ul>
                  <p className="text-xs text-[#3B3535]/60 leading-relaxed"><strong>Best for:</strong> {p.bestFor}</p>
                </div>
              ))}
            </div>
            <Link to="/providers" className="inline-flex items-center gap-1.5 mt-6 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14]">
              See the provider page <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>

        {/* Active homes */}
        <section className="py-16 bg-[#FFFAF8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="active-homes" icon={Home} subtitle="Definitions" title={d.activeHomes.heading} />
            <div className="bg-white rounded-2xl p-7 shadow-sm border border-gray-100">
              <p className="text-[#3B3535] leading-relaxed mb-4">{d.activeHomes.definition}</p>
              <p className="text-sm text-[#3B3535]/70 leading-relaxed">{d.activeHomes.note}</p>
            </div>
          </div>
        </section>

        {/* Staff roles */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="staff-roles" icon={Users} subtitle="Definitions" title={d.staffRoles.heading} />
            <p className="text-[#3B3535]/75 leading-relaxed mb-8 max-w-3xl">{d.staffRoles.intro}</p>
            <div className="space-y-4">
              {(d.staffRoles.roles || []).map((r) => (
                <div key={r.name} className="flex items-start gap-4 bg-[#FFF8F5] rounded-2xl p-5 border border-[#B5541B]/10">
                  <div className="w-10 h-10 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] shrink-0">
                    {getIcon(r.icon, 'w-5 h-5')}
                  </div>
                  <div>
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="font-bold text-[#1F1E1E]">{r.name}</h3>
                      <span className="text-[10px] uppercase tracking-wide font-bold bg-[#B5541B]/10 text-[#B5541B] px-2 py-0.5 rounded-full">{r.paid}</span>
                    </div>
                    <p className="text-sm text-[#3B3535]/75 leading-relaxed">{r.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-sm text-[#3B3535]/70 leading-relaxed mt-6">{d.staffRoles.note}</p>
          </div>
        </section>

        {/* Sponsorship */}
        <section className="py-16 bg-[#1F1E1E] text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div id="sponsorship" className="mb-8 scroll-mt-28">
              <div className="inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#D4722F] mb-3">
                <Handshake className="w-4 h-4" />
                Sponsorship
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold" data-testid="fees-sponsorship-heading">{d.sponsorship.heading}</h2>
            </div>
            <p className="text-white/80 leading-relaxed mb-6 max-w-3xl">{d.sponsorship.intro}</p>
            {d.sponsorship.ownershipStatement && (
              <p className="text-base font-semibold text-white leading-relaxed mb-3 max-w-3xl" data-testid="fees-ownership-statement">
                {d.sponsorship.ownershipStatement}
              </p>
            )}
            <p className="text-sm text-white/70 mb-10">
              <Link to="/privacy" className="underline text-[#D4722F] hover:text-white transition-colors">
                Read how sharing permissions and information access work in our privacy policy
              </Link>
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white/5 rounded-2xl p-7 border border-white/10">
                <h3 className="font-bold text-lg mb-4 text-[#D4722F]">{d.sponsorship.startsHeading}</h3>
                <ul className="space-y-3">
                  {(d.sponsorship.starts || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/80 leading-relaxed">
                      <Check className="w-4 h-4 text-[#D4722F] mt-0.5 shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-white/5 rounded-2xl p-7 border border-white/10">
                <h3 className="font-bold text-lg mb-4 text-[#D4722F]">{d.sponsorship.endsHeading}</h3>
                <ul className="space-y-3">
                  {(d.sponsorship.ends || []).map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-white/80 leading-relaxed">
                      <Check className="w-4 h-4 text-[#D4722F] mt-0.5 shrink-0" />{s}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-10 bg-gradient-to-r from-[#B5541B] to-[#8B3F14] rounded-2xl p-7 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
              <div>
                <h3 className="font-bold text-lg mb-1">{d.sponsorship.ctaTitle}</h3>
                <p className="text-sm text-white/85">{d.sponsorship.ctaDescription}</p>
              </div>
              <button
                onClick={() => setSponsorOpen(true)}
                data-testid="fees-sponsor-cta"
                className="shrink-0 inline-flex items-center gap-2 bg-white text-[#B5541B] hover:bg-white/90 font-semibold px-6 py-3 rounded-full transition-all"
              >
                {d.sponsorship.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        {/* Founder + sponsorship example */}
        <section className="py-16 bg-[#FFFAF8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="founder-example" icon={Calculator} subtitle="Worked example" title={d.founderExample.heading} />
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden" data-testid="founder-example-card">
              <dl>
                {(d.founderExample.rows || []).map((r, i) => (
                  <div key={r.label} className={`flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 px-6 py-4 ${i % 2 === 1 ? 'bg-[#FFF8F5]' : ''}`}>
                    <dt className="sm:w-1/2 text-sm font-semibold text-[#1F1E1E]">{r.label}</dt>
                    <dd className="sm:w-1/2 text-sm text-[#3B3535]/85">{r.value}</dd>
                  </div>
                ))}
              </dl>
              <div className="px-6 py-4 border-t border-[#B5541B]/10 flex items-start gap-2.5">
                <Info className="w-4 h-4 text-[#B5541B] shrink-0 mt-0.5" />
                <p className="text-sm text-[#3B3535]/75 leading-relaxed">{d.founderExample.note}</p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="faq" icon={HelpCircle} subtitle="Common questions" title={d.faq.heading} />
            <Accordion type="single" collapsible className="w-full" data-testid="fees-faq">
              {(d.faq.items || []).map((item, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[#B5541B]/15">
                  <AccordionTrigger
                    data-testid={`faq-question-${i}`}
                    className="text-base font-semibold text-[#1F1E1E] hover:text-[#B5541B] hover:no-underline py-5"
                  >
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent data-testid={`faq-answer-${i}`}>
                    {(item.answer || []).map((p, pi) => (
                      <p key={pi} className="text-sm text-[#3B3535]/85 leading-relaxed mb-3 last:mb-0 max-w-3xl">{p}</p>
                    ))}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Add-ons & overage */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="add-ons" icon={PlusCircle} subtitle="Beyond the plans" title={d.addOns.heading} />
            <div className="grid sm:grid-cols-2 gap-x-8 gap-y-2 mb-12">
              {(d.addOns.items || []).map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-[#3B3535]/80 py-1">
                  <Check className="w-4 h-4 text-[#B5541B] mt-0.5 shrink-0" />{a}
                </div>
              ))}
            </div>
            <h3 className="font-bold text-[#1F1E1E] mb-4">{d.addOns.overageHeading}</h3>
            <div className="space-y-3 mb-6">
              {(d.addOns.overages || []).map((o) => (
                <div key={o.item} className="bg-[#FFF8F5] rounded-xl p-5 border border-[#B5541B]/10">
                  <p className="font-semibold text-sm text-[#1F1E1E] mb-1">{o.item}</p>
                  <p className="text-sm text-[#3B3535]/75">{o.rates}</p>
                </div>
              ))}
            </div>
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <Info className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800 leading-relaxed">{d.addOns.caveat}</p>
            </div>
          </div>
        </section>

        {/* Pilot */}
        <section className="py-16 bg-[#FFFAF8]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="pilot" icon={Rocket} subtitle="Limited offer" title={d.pilot.heading} />
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <p className="text-[#3B3535] leading-relaxed mb-6">{d.pilot.description}</p>
              <div className="grid sm:grid-cols-2 gap-4 mb-6">
                <div className="bg-[#FFF8F5] rounded-xl p-5 border border-[#B5541B]/10">
                  <p className="text-2xl font-bold text-[#B5541B] mb-1">{d.pilot.priceOne}</p>
                  <p className="text-sm text-[#3B3535]/75">{d.pilot.priceOneNote}</p>
                </div>
                <div className="bg-[#FFF8F5] rounded-xl p-5 border border-[#B5541B]/10">
                  <p className="text-2xl font-bold text-[#B5541B] mb-1">{d.pilot.priceTwo}</p>
                  <p className="text-sm text-[#3B3535]/75">{d.pilot.priceTwoNote}</p>
                </div>
              </div>
              <Link
                to="/providers#pilot"
                data-testid="fees-pilot-cta"
                className="inline-flex items-center gap-2 bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold px-6 py-3 rounded-full transition-all"
              >
                {d.pilot.ctaLabel}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </section>

        {/* Principles */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeading id="principles" icon={Scale} subtitle="Rationale" title={d.principles.heading} />
            <div className="grid md:grid-cols-2 gap-5">
              {(d.principles.items || []).map((p) => (
                <div key={p.title} className="bg-[#FFF8F5] rounded-2xl p-6 border border-[#B5541B]/10">
                  <div className="w-10 h-10 rounded-xl bg-[#B5541B]/10 flex items-center justify-center text-[#B5541B] mb-3">
                    <HandCoins className="w-5 h-5" />
                  </div>
                  <h3 className="font-bold text-[#1F1E1E] mb-2">{p.title}</h3>
                  <p className="text-sm text-[#3B3535]/75 leading-relaxed">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <SponsorReferralModal open={sponsorOpen} onOpenChange={setSponsorOpen} />
      <Footer />
      <BackToTop />
    </div>
  );
};

export default FeesPage;
